// Main Quick Add orchestrator
import { addToCart, t, getCurrentLocale } from 'lib-utils';
import MobileSlideout from 'mobile-slideout';
import DesktopPopover from 'desktop-popover';

class QuickAdd {
  constructor() {
    this.mobileSlideout = new MobileSlideout();
    this.desktopPopover = new DesktopPopover();
    this.variantCache = new Map();
    this.isMobile = window.innerWidth < 750;

    this._attachListeners();
    this._handleResize();
  }

  _attachListeners() {
    // Event delegation for quick add buttons
    document.addEventListener('click', (e) => {
      const button = e.target.closest('.quick-add-btn');
      if (button && !button.classList.contains('is-loading')) {
        e.preventDefault();
        e.stopPropagation();
        this.handleQuickAddClick(button);
      }
    });

    // Update mobile state on resize
    window.addEventListener('resize', () => this._handleResize());
  }

  _handleResize() {
    this.isMobile = window.innerWidth < 750;
  }

  // Main entry point when quick add button is clicked
  async handleQuickAddClick(button) {
    const card = button.closest('.product-card');
    const productData = this._getProductDataFromCard(card, button);

    // Check if product is a bundle using multiple indicators
    const isBundle =
      // Check if handle starts with "bundle-"
      productData.handle.toLowerCase().startsWith('bundle-') ||
      // Check if there's a bundle badge
      card
        .querySelector('.badge')
        ?.textContent.toLowerCase()
        .includes('bundle') ||
      // Check the badge text specifically
      card
        .querySelector('.badges')
        ?.textContent.toLowerCase()
        .includes('bundle');

    if (isBundle) {
      // Redirect to product page for bundles
      window.location.href = `/products/${productData.handle}`;
      return;
    }

    // Determine flow based on variants - open slideout for any product with variants
    const hasAnyVariants = productData.hasVariants === 'true';

    if (!hasAnyVariants) {
      // Simple flow: truly no variants (single color, no size)
      await this._handleSimpleAdd(button, productData);
    } else {
      // Complex flow: show slideout for any product with variants
      await this._handleVariantSelection(button, productData);
    }
  }

  // Flow for products without size variants
  async _handleSimpleAdd(button, productData) {
    try {
      const product = await this._fetchVariants(productData.handle);
      const availableVariant = product.variants.find((v) => v.available);

      if (!availableVariant) {
        console.error('No available variants found');
        return;
      }

      this._setButtonState(button, 'loading');
      await this._addToCart(availableVariant.id, 1);
      this._setButtonState(button, 'added');

      setTimeout(() => {
        this._setButtonState(button, 'default');
        this._openCartDrawer();
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      this._setButtonState(button, 'default');
    }
  }

  // Flow for products with any variants (size, color, etc)
  async _handleVariantSelection(button, productData) {
    try {
      // Fetch product
      const product = await this._fetchVariants(productData.handle);

      // Check if product has sizes (desktop popover is only for size selection)
      // Common size translations: English, German, French, Dutch, Spanish, Italian (singular, plural, and tamaño for product sizes)
      const sizeKeywords = ['size', 'größe', 'taille', 'maat', 'talla', 'tallas', 'tamaño', 'tamaña', 'tamañas', 'dimensione', 'taglia'];
      const sizes = product.options.find((opt) =>
        sizeKeywords.includes(opt.name.toLowerCase().trim())
      );
      const hasSizes = sizes && sizes.values.length > 1;

      // Desktop: only show popover if product has multiple sizes
      // Mobile: show slideout for any variants
      if (this.isMobile) {
        // Mobile slideout handles both color and size selection
        this._showMobileSlideout(button, productData, product);
      } else {
        // Desktop behavior
        if (hasSizes) {
          // Has sizes: show popover for size selection
          this._showDesktopPopover(button, productData, product);
        } else {
          // Color-only or single variant: add to cart directly
          // Get selected color from card swatches
          const card = button.closest('.product-card');
          const activeSwatch = card.querySelector(
            '.product-card__swatch.active'
          );
          const selectedVariantId = activeSwatch?.dataset.variantId;

          const variant = selectedVariantId
            ? product.variants.find((v) => v.id === parseInt(selectedVariantId))
            : product.variants.find((v) => v.available);

          if (variant && variant.available) {
            this._setButtonState(button, 'loading');
            await this._addToCart(variant.id, 1);
            this._setButtonState(button, 'added');

            setTimeout(() => {
              this._setButtonState(button, 'default');
              this._openCartDrawer();
            }, 2000);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching variants:', error);
    }
  }

  // Desktop: Show size selector popover
  _showDesktopPopover(button, productData, product) {
    this.desktopPopover.show(button, product, async (selectedSize) => {
      // Get selected color from card swatches
      const card = button.closest('.product-card');
      const activeSwatch = card.querySelector('.product-card__swatch.active');
      const selectedColor = activeSwatch
        ? this._getColorFromVariantId(
            product.variants,
            activeSwatch.dataset.variantId,
            product
          )
        : null;

      // Find matching variant
      const variant = this._findMatchingVariant(
        product.variants,
        selectedColor,
        selectedSize,
        product
      );

      if (variant && variant.available) {
        try {
          this._setButtonState(button, 'loading');
          this.desktopPopover.close();

          await this._addToCart(variant.id, 1);

          this._setButtonState(button, 'added');

          setTimeout(() => {
            this._setButtonState(button, 'default');
            this._openCartDrawer();
          }, 800);
        } catch (error) {
          this._setButtonState(button, 'default');
        }
      }
    });
  }

  _showMobileSlideout(button, productData, product) {
    const card = button.closest('.product-card');
    const swatchData = this._extractSwatchDataFromCard(card);

    // Pass the size guide HTML along with other data
    const productInfoWithSizeGuide = {
      ...productData,
      sizeGuideHtml: productData.sizeGuideHtml,
    };

    this.mobileSlideout.show(
      productInfoWithSizeGuide,
      product,
      swatchData,
      async (variantId) => {
        await this._addToCart(variantId, 1);

        // Update button text
        this.mobileSlideout.setButtonText(t('products.product.added_to_cart'));

        setTimeout(() => {
          this.mobileSlideout.close();
          this._openCartDrawer();
        }, 800);
      }
    );
  }

  // Extract swatch background styles from product card
  _extractSwatchDataFromCard(card) {
    const swatches = card.querySelectorAll('.product-card__swatch');
    const swatchData = [];

    swatches.forEach((swatch) => {
      const variantId = swatch.dataset.variantId;
      const style = window.getComputedStyle(swatch);
      const backgroundImage = style.backgroundImage;
      const backgroundColor = style.backgroundColor;

      swatchData.push({
        variantId: variantId,
        backgroundImage: backgroundImage !== 'none' ? backgroundImage : null,
        backgroundColor: backgroundColor,
      });
    });

    return swatchData;
  }

  // Fetch product variants from Shopify API
  async _fetchVariants(handle) {
    // Include locale prefix for translated content
    const locale = getCurrentLocale();
    const localePrefix = locale !== 'en' ? `/${locale}` : '';
    const cacheKey = `${locale}:${handle}`;

    // Check cache first
    if (this.variantCache.has(cacheKey)) {
      return this.variantCache.get(cacheKey);
    }

    try {
      const response = await fetch(`${localePrefix}/products/${handle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();

      // Cache the full product data
      this.variantCache.set(cacheKey, product);

      return product;
    } catch (error) {
      console.error('Error fetching variants:', error);
      throw error;
    }
  }

  // Add item to cart using Shopify Ajax API
  async _addToCart(variantId, quantity = 1) {
    try {
      // Use your existing addToCart utility
      await addToCart({ id: variantId, quantity: quantity }, false); // Don't auto-open cart

      // Trigger bndlr discount updates if available
      if (
        window.bndlr &&
        typeof window.bndlr.updateCartDiscounts === 'function'
      ) {
        window.bndlr.updateCartDiscounts();
      }

      // Manually trigger cart events with proper timing
      document.dispatchEvent(new CustomEvent('nova:cart:refresh'));

      return { success: true };
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  }

  _openCartDrawer() {
    // Use your existing cart open event
    document.dispatchEvent(new CustomEvent('nova:cart:open'));
  }

  // Update button visual state
  _setButtonState(button, state) {
    button.classList.remove('is-loading', 'is-added');

    const originalHTML = button.dataset.originalHtml || button.innerHTML;
    if (!button.dataset.originalHtml) {
      button.dataset.originalHtml = button.innerHTML;
    }


    switch (state) {
      case 'loading':
        button.classList.add('is-loading');
        button.innerHTML = t('products.product.adding');
        button.disabled = true;
        break;

      case 'added':
        button.classList.add('is-added');
        button.innerHTML = t('products.product.added_to_cart');
        button.disabled = true;
        break;

      case 'default':
        button.innerHTML = originalHTML;
        button.disabled = false;
        break;
    }
  }

  _getProductDataFromCard(card, button) {
    const primaryImage = card.querySelector('.product-card__image--primary');
    const title = card.querySelector('.product-card__title');
    const priceElement = card.querySelector('.product-card__price');

    // Extract price info
    let price = null;
    let compareAtPrice = null;
    let discountPercentage = null;

    if (priceElement) {
      const salePrice = priceElement.querySelector('.price-item--sale');
      const regularPrice = priceElement.querySelector('.price-item--regular');
      const discountBadge = priceElement.querySelector(
        '.price__discount-detail'
      );

      if (salePrice) {
        price = salePrice.textContent.trim();
      } else {
        const regularPriceOnly = priceElement.querySelector('.price');
        if (regularPriceOnly) {
          price = regularPriceOnly.textContent.trim();
        }
      }

      if (regularPrice) {
        compareAtPrice = regularPrice.textContent.trim();
      }

      if (discountBadge) {
        discountPercentage = discountBadge.textContent.trim();
      }
    }

    // Decode the HTML entities in size guide
    let sizeGuideHtml = null;
    if (card.dataset.sizeGuide) {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = card.dataset.sizeGuide;
      sizeGuideHtml = textarea.value;
    }

    return {
      id: card.dataset.productId,
      handle: card.dataset.productHandle,
      hasVariants: button.dataset.hasVariants,
      colorCount: button.dataset.colorCount,
      available: card.dataset.available,
      sizeGuideHtml: sizeGuideHtml,
      image: primaryImage?.src || '',
      title: title?.textContent || 'Product',
      price: price,
      compareAtPrice: compareAtPrice,
      discountPercentage: discountPercentage,
    };
  }

  // Find the option position for color
  _findColorOptionPosition(product) {
    const colorKeywords = ['color', 'colour', 'farbe', 'couleur', 'kleur', 'colore'];
    const colorOption = product.options.find((opt) =>
      colorKeywords.includes(opt.name.toLowerCase().trim())
    );
    return colorOption ? colorOption.position : null;
  }

  // Find the option position for size
  _findSizeOptionPosition(product) {
    const sizeKeywords = ['size', 'größe', 'taille', 'maat', 'talla', 'tallas', 'tamaño', 'tamaña', 'tamañas', 'dimensione', 'taglia'];
    const sizeOption = product.options.find((opt) =>
      sizeKeywords.includes(opt.name.toLowerCase().trim())
    );
    return sizeOption ? sizeOption.position : null;
  }

  // Find variant matching color and size
  _findMatchingVariant(variants, color, size, product) {
    const colorPos = this._findColorOptionPosition(product);
    const sizePos = this._findSizeOptionPosition(product);

    return variants.find((variant) => {
      // If no color selection needed (single color or no color option)
      if (!color) {
        // Match size in the correct position
        if (sizePos) {
          return variant[`option${sizePos}`] === size;
        }
        // Fallback: try matching size in any position
        return variant.option1 === size || variant.option2 === size;
      }

      // For products with both color and size
      const variantColor = colorPos ? variant[`option${colorPos}`] : variant.option1;
      const variantSize = sizePos ? variant[`option${sizePos}`] : variant.option2;
      return variantColor === color && variantSize === size;
    });
  }

  // Get color value from variant ID
  _getColorFromVariantId(variants, variantId, product) {
    const variant = variants.find((v) => v.id === parseInt(variantId));
    if (!variant) return null;

    const colorPos = this._findColorOptionPosition(product);
    return colorPos ? variant[`option${colorPos}`] : variant.option1;
  }
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new QuickAdd();
  });
} else {
  new QuickAdd();
}

export default QuickAdd;
